import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { BookmarkModel } from '../bookmarks/models/bookmark.model';

import { StoryModel } from '../story/models/story.model';
import { siteNavigation } from '../shared/features/site-navigation/data/site-navigation';
import { getAllCategories } from '../shared/features/site-navigation/site-navigation-utils';
import { normalizeBookmarkModel } from '../bookmarks/lib/bookmark-utils';

const webOnlyTags = ['live'];

@Injectable({
  providedIn: 'root',
})
export class ExploreService {
  constructor(private http: HttpClient) {}

  search(
    keyword: string = '',
    tags: string[] = [],
    lang: string = 'en',
  ): Observable<BookmarkModel[]> {
    // seperate keywords
    const queryKeywords = keyword.split(/[ ]+/).join('+');

    // quote seperate tags
    const queryTags = webOnlyTags
      .concat(tags)
      .filter((tag) => tag !== '' && tag != null)
      .map((tag) => `"${tag}"`)
      .join('+');

    return this.http
      .get(`/search?k=${queryKeywords}&t=${queryTags}&il=${lang}&ol=${lang}&odns=true`)
      .pipe(map((result: any[]) => this.resultsToBookmarks(result)));
  }

  searchInternal(keyword = '', directory = '') {
    return this.http
      .get(`/ws/search/internal?k=${keyword}&dir=${directory}`)
      .pipe(map((result: any[]) => this.resultsToBookmarks(result)));
  }

  getAnatomyCategory(
    query: string,
    category: string,
    tags?: string[],
    lang?: string,
  ): Observable<BookmarkModel[]> {
    return this.search(query, [category, ...tags], lang);
  }

  // get condition model search result
  getConditionModel(id: string): Observable<BookmarkModel> {
    const dbID = this.toDatabaseContentId(id);

    return this.http.get(`/search/conditions?q=${dbID}`).pipe(
      map((result: any[]) => this.resultsToBookmarks(result)),
      map((bookmarks) => bookmarks[0]),
    );
  }

  private resultsToBookmarks(result: any[]): BookmarkModel[] {
    result.forEach((bookmark) => {
      bookmark = normalizeBookmarkModel(bookmark);
    });
    return plainToClass(BookmarkModel, result);
  }

  // Bookmark category is currently embedded as a tag inside a string array, making it
  // difficult to get to. This function checks for matches in the tags against the
  // menu system and attempts to find match.
  getBookmarkCategory(bookmark: BookmarkModel): string {
    if (!bookmark.tags) {
      return null;
    }

    const tags = siteNavigation.categories.map((category) => category.id.toLocaleLowerCase());
    return bookmark.tags.find((tag) => tags.includes(tag.toLocaleLowerCase()));
  }

  getStoryCategory(story: StoryModel): string {
    if (!story.tags) {
      return null;
    }

    const tags = getAllCategories().map((category) => category.id.toLocaleLowerCase());
    return story.tags.find((tag) => tags.includes(tag.toLocaleLowerCase()));
  }

  // Normalizes id to app formatted id (i.e., production/maleAdult/flu)
  toAppContentId(id: string): string {
    return id.replace('Condition.content.', '').replace('.json', '').split('.').join('/');
  }

  // Normalizes id to DB format (i.e., Condition.content.production.maleAdult.flu.json)
  toDatabaseContentId(id: string): string {
    if (!id.match('Condition.content')) {
      const normalizedId = id.replace('.json', '').split('/').join('.');
      return `Condition.content.${normalizedId}.json`;
    } else {
      return id;
    }
  }
}
